import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ENV_CONFIG } from '@digilize/shared/utils/tokens';
import { Buyer, EnvConfig, OrderStatus, ProductDisplayStep } from '@shared/definitions';
import {
  BehaviorSubject,
  combineLatest,
  concatMap,
  filter,
  first,
  forkJoin,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';

import { AppContext } from '../application/app.context';
import { CartState } from '../application/states/cart.state';
import { CheckoutState } from '../application/states/checkout.state';
import { OrderState } from '../application/states/order.state';
import { ReservationState } from '../application/states/reservation.state';
import { SeatsState } from '../application/states/seats.state';
import { ShowState } from '../application/states/show.state';
import { SeatSelectionType } from '../models/checkout';
import { Product, Supplement } from '../models/product';
import { ReservationUserData } from '../models/reservation';
import { Show } from '../models/show';
import { AddonsStep, PaymentStep, ReservingStep, Step, SupplementsStep } from '../models/step';
import { findNewestOrder } from '../utils/order.utils';
import { CartsApiService } from './carts.service';
import { CartDto, CartStatus, IntegrationReservation } from './dto/products.dto';
import { ProductsService } from './products.service';
import { ReservingService } from './reserving.service';

@Injectable({
  providedIn: 'root',
})
export class StateService {
  private _lastPossibleStep = new Subject<Step>();
  lastPossibleStep$ = this._lastPossibleStep.asObservable();
  set lastPossibleStep(lastPossibleStep: Step) {
    this._lastPossibleStep.next(lastPossibleStep);
  }
  private _reinitializing = new BehaviorSubject(false);
  reinitializing$ = this._reinitializing.asObservable();
  get reinitializing() {
    return this._reinitializing.value;
  }
  set reinitializing(reinitializing: boolean) {
    this._reinitializing.next(reinitializing);
  }
  constructor(
    @Inject(ENV_CONFIG) private readonly config: EnvConfig,
    private checkoutState: CheckoutState,
    private cartState: CartState,
    private reservationState: ReservationState,
    private cartsApiService: CartsApiService,
    private productsService: ProductsService,
    private showState: ShowState,
    private seatsState: SeatsState,
    private reserveringService: ReservingService,
    private appContext: AppContext,
    private orderState: OrderState,
    private router: Router
  ) {}

  fetchAndReinitFromActiveCartId(cartIdToReinit: string | null): Observable<ReinitDTO | null> {
    this.reinitializing = true;

    if (!cartIdToReinit) {
      this.reinitializing = false;

      return of(null);
    }

    return this.cartsApiService.getCart(cartIdToReinit).pipe(
      switchMap((cart) => {
        this.cartState.regenerateDeliveryMethod(cart.delivery_method);
        const isFailedOrder =
          cart.status === CartStatus.Ordered &&
          (!this.orderState.orderSessionStatus || this.orderState.didOrderFail(this.orderState.orderSessionStatus));

        if (
          !isFailedOrder &&
          !cart.reservation_code &&
          cart.status !== CartStatus.Active &&
          cart.status !== CartStatus.Paid &&
          cart.status !== CartStatus.Ordered
        ) {
          this.reinitializing = false;
          return of(null);
        }

        const showId = cart?.summary?.lines?.[0]?.movie_show_id;
        if (!showId) {
          this.reinitializing = false;

          return of(null);
        }
        this.appContext.setPosId(cart.pos_id);
        return forkJoin({
          productsByShow: this.showState.products$.pipe(
            first(),
            switchMap((products) => (products === null ? this.showState.fetchProducts(showId) : of(products)))
          ),
          addons: this.showState.addons$.pipe(
            first(),
            switchMap((addons) => (addons === null ? this.showState.fetchAddons(cart.pos_id) : of(addons)))
          ),
          restaurantReservations: of(null),
          // The cart's seated lines are read back through the seat catalogue, so it is joined here
          // rather than waited on before the cart: both round trips are now in flight together.
          seats: this.showState.show$.pipe(
            filter((show) => !!show),
            first(),
            concatMap((show) => this.seatsState.loadCatalogue(show.shopId, show.areaId))
          ),
        }).pipe(
          withLatestFrom(this.showState.show$.pipe(first())),
          map(([{ productsByShow, addons }, show]) => ({
            cart,
            products: [...productsByShow, ...addons],
            integrationReservations: cart.summary.integration_reservations.filter((r) => r.type === 'etender'),
            show,
          })),
          tap((resp) => {
            this.cartState.setCart(resp.cart, resp.products);
          }),
          withLatestFrom(
            this.cartState.withReservation(),
            this.cartState.tickets$,
            this.checkoutState.seatSelectionType$.pipe(filter((type) => type !== null))
          ),
          map(([resp, requiresReservation, tickets, seatSelectionType]) => {
            return { ...resp, requiresReservation, tickets, seatSelectionType };
          }),
          tap(({ tickets }) => tickets && this.seatsState.init(tickets)),
          map((resp) => ({ ...resp, selectedSeats: this.seatsState.selected })),
          tap((resp) => resp && this.reinitState(resp, resp.show))
        );
      })
    );
  }

  reinitState(state: ReinitDTO, show: Show) {
    let lastPossibleStep: Step = null;
    const { cart, products, integrationReservations } = state;

    if (cart.reservation_code) {
      this.router.navigate(['/reservation', cart.reservation_code]);
      this.reinitializing = false;
      return;
    }

    this.setUserData(cart.buyer);
    this.cartState.startTimerIfLimited(cart?.reserved_until, cart?.server_time);

    state.cart.summary.lines
      .filter((line) => line.cineville_barcode)
      .forEach((line) => {
        this.reservationState.addCvCard({
          barcode: line.cineville_barcode,
          cartLineId: line.id,
          cardId: line.cineville_barcode,
          name: undefined,
        });
      });
    // A line echoes its `subscription_card_code`, but not which saved card it came from or under
    // what label — that mapping only ever existed client-side, so it is rehydrated from the
    // sessionStorage copy written by the information page.
    this.reservationState.restoreSubscriptionCards(cart.id);
    this.setSkippedSteps()
      .pipe(first())
      .subscribe(() => {
        this.tryToChangeReservingStepOrder(state.cart.id);
        const newestOrder = findNewestOrder(state.cart.summary.orders);
        const isOnPaymentStep = this.checkoutState.findCurrentStep() instanceof PaymentStep;

        const ensureOrder$: Observable<unknown> =
          isOnPaymentStep && !newestOrder
            ? this.reservationState.reservationUserData$.pipe(
                first(),
                concatMap((userData) => this.cartState.createPendingOrder(userData))
              )
            : of(null);

        ensureOrder$.subscribe({
          next: () => {
            if (newestOrder?.status === OrderStatus.Paid) {
              this.router.navigate(['/order', newestOrder.id]);
              this.reinitializing = false;
              return;
            }
            if (
              newestOrder &&
              (newestOrder.status === OrderStatus.Processing || newestOrder.status === OrderStatus.PartiallyPaid)
            ) {
              const paymentStep = this.checkoutState.steps.find((step) => step instanceof PaymentStep);

              if (paymentStep && this.checkoutState.findCurrentStep()?.route !== paymentStep.route) {
                this.lastPossibleStep = paymentStep;
              }
              this.reinitializing = false;
              return;
            }

            const currentStep = this.checkoutState.findCurrentStep();
            if (!currentStep) {
              console.error(
                `[Checkout] reinitState: unrecognized route "${this.checkoutState.getStepRoute()}" is not a valid step in the current flow (URL: ${
                  window.location.href
                }). Navigating to the furthest reachable step.`
              );
            }

            for (const step of this.checkoutState.steps) {
              const canNavigate = step.canBeNavigatedTo(state, show);
              if (canNavigate) {
                lastPossibleStep = step;
                if (currentStep && step.route === currentStep.route) {
                  break;
                }
              } else if (!step.skipped) {
                break;
              }
            }

            if (lastPossibleStep && currentStep !== lastPossibleStep) {
              this.lastPossibleStep = lastPossibleStep;
            }

            this.reinitializing = false;
          },
          error: () => {
            this.reinitializing = false;
          },
        });
      });
  }

  tryToChangeReservingStepOrder(cartId: string): void {
    const reservingStepIndex = Number.parseInt(localStorage.getItem(`${cartId}-reservingStep`));
    if (reservingStepIndex) {
      this.checkoutState.moveReservingStepToIndexPosition(reservingStepIndex, cartId);
      return;
    }
    if (this.checkoutState.findCurrentStep() instanceof ReservingStep) {
      this.checkoutState.moveReservingStepToIndexPosition(1, cartId);
    } else {
      this.checkoutState.moveReservingStepBeforePaymentStep(cartId);
    }
  }

  setSkippedSteps(): Observable<any> {
    const supplements$ = this.cartState.tickets$.pipe(
      concatMap((tickets) => {
        if (!tickets.length || tickets.length === 0) {
          return of([]);
        }
        const productIds = tickets.map((ticket) => ticket.product.id);
        const uniqueProductsIds = new Set(productIds);
        return this.showState.fetchSupplements(Array.from(uniqueProductsIds), ProductDisplayStep.SupplementsPage);
      })
    );
    // Already answered by the bootstrap wave in every path that reaches here; re-read only if not.
    const addons$ = this.showState.loadedAddons
      ? of(this.showState.loadedAddons)
      : this.showState.fetchAddons(this.appContext.posId);
    return combineLatest([supplements$, addons$, this.tryToSkipReservingStep()]).pipe(
      tap(([supplements, addons]) => {
        this.tryToSkipSupplementsStep(supplements);
        this.tryToSkipAddonsStep(addons);
      })
    );
  }

  public tryToSkipReservingStep(): Observable<boolean> {
    return this.cartState.withReservation().pipe(
      first(),
      tap((requireReservation) => {
        this.checkoutState.setStepSkipped(ReservingStep, !requireReservation);
      })
    );
  }

  public tryToSkipSupplementsStep(supplements: Supplement[]): void {
    this.checkoutState.setStepSkipped(SupplementsStep, supplements.length === 0);
  }

  public tryToSkipAddonsStep(addOns: Product[]): void {
    this.checkoutState.setStepSkipped(AddonsStep, !(addOns.length > 0));
  }

  private setUserData(buyer: Buyer) {
    if (!buyer) {
      return;
    }
    const reservationUserData: ReservationUserData = {
      email: buyer.email,
      firstname: buyer.first_name,
      lastname: buyer.last_name,
      phoneNumber: buyer.phone,
      newsletter: buyer.newsletter,
    };
    this.reservationState.setReservationUserData(reservationUserData);
  }
}

export interface ReinitDTO {
  cart: CartDto;
  products: Product[];
  selectedSeats: Set<string>;
  seatSelectionType: SeatSelectionType;
  requiresReservation: boolean;
  integrationReservations: IntegrationReservation[] | null;
  show: Show;
}
